import React, { useState } from 'react';
import { View, TouchableOpacity, Image as RNImage, ActivityIndicator } from 'react-native';
import { useForm } from 'react-hook-form';
import { Ionicons } from '@expo/vector-icons';

import tw from '../../../../styles/tailwind';
import Text from '../../../../components/Text';
import Modal from '../../../../components/Modal';
import Toggle from '../../../../components/Toggle';
import Input from '../../../../components/Forms/Input';
import IconButton from '../../../../components/IconButton';
import useFormSubmit from '../../../../hooks/useFormSubmit';
import api from '../../../../utils/api';


export default function NewTicketModal({ communityId, areas, categories = [], onRequestClose, onCreated }: any) {
  const { control, handleSubmit, watch, setValue } = useForm<any>({
    defaultValues: { description: '', area_uuid: '', category_uuid: '', is_high_priority: false },
  });

  const [files, setFiles] = useState<any[]>([]);
  const [uploading, setUploading] = useState(false);
  // Si el área se eligió a mano, la categoría ya no la cambia (igual que en el backend).
  const [areaTouched, setAreaTouched] = useState(false);

  const { submitForm, isSubmitting } = useFormSubmit({
    onSuccess: onCreated,
    onError: () => {},
    successToastMessage: 'Reporte creado',
    endpoint: `/communities/${communityId}/tickets`,
  });

  const areaUuid = watch('area_uuid');
  const categoryUuid = watch('category_uuid');

  const selectArea = (uuid: string) => {
    setValue('area_uuid', uuid);
    setAreaTouched(true);
  };

  const selectCategory = (uuid: string) => {
    setValue('category_uuid', uuid);
    if (areaTouched) return;
    const category = categories.find((c: any) => c.uuid === uuid);
    const defaultArea = category?.default_area ?? category?.defaultArea;
    setValue('area_uuid', defaultArea?.uuid ?? '');
  };
  const isHighPriority = watch('is_high_priority');

  const onFileSelect = async (e: any) => {
    const selected = Array.from(e.target.files ?? []) as File[];
    if (selected.length === 0) return;

    setUploading(true);
    try {
      const uploaded: any[] = [];
      for (const file of selected) {
        const fd = new FormData();
        fd.append('image', file);
        const res = await api.post(`/communities/${communityId}/images`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        uploaded.push(res.data);
      }
      setFiles(prev => [...prev, ...uploaded]);
    } catch (err: any) {
      alert(err?.response?.data?.message ?? 'Error subiendo imagen');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const removeFile = (uuid: string) => {
    setFiles(prev => prev.filter(f => f.uuid !== uuid));
  };

  const onSave = (data: any) => {
    submitForm({
      ...data,
      file_uids: files.map(f => f.uuid),
    });
  };

  return (
    <Modal onRequestClose={onRequestClose} title="Nuevo reporte" style={{ modal: tw`w-[560px]` }}>
      <View style={tw`pt-2`}>
        <Text style={tw`text-sm text-gray-600 mb-1`}>Descripción</Text>
        <Input control={control} name="description" rules={{ required: true }} multiline numberOfLines={4} />

        {categories.length > 0 && (
          <>
            <Text style={tw`text-sm text-gray-600 mb-2`}>Categoría (opcional)</Text>
            <View style={tw`flex-row flex-wrap gap-2 mb-5`}>
              <PillChoice
                label="Sin categoría"
                active={!categoryUuid}
                onPress={() => selectCategory('')}
              />
              {categories.map((c: any) => (
                <PillChoice
                  key={c.uuid}
                  label={c.name}
                  active={categoryUuid === c.uuid}
                  onPress={() => selectCategory(c.uuid)}
                />
              ))}
            </View>
          </>
        )}

        <Text style={tw`text-sm text-gray-600 mb-2`}>Área (opcional)</Text>
        <View style={tw`flex-row flex-wrap gap-2 mb-5`}>
          <PillChoice
            label="Por asignar"
            active={!areaUuid}
            onPress={() => selectArea('')}
          />
          {areas.map((a: any) => (
            <PillChoice
              key={a.uuid}
              label={a.name}
              active={areaUuid === a.uuid}
              onPress={() => selectArea(a.uuid)}
            />
          ))}
        </View>

        <View style={tw`flex-row items-center justify-between py-2 mb-3`}>
          <Text style={tw`text-sm text-gray-700 font-500`}>Prioridad alta</Text>
          <Toggle
            value={!!isHighPriority}
            onValueChange={(v) => setValue('is_high_priority', v)}
          />
        </View>

        {/* Evidencia */}
        <Text style={tw`text-sm text-gray-600 mb-2`}>Evidencia (opcional)</Text>
        <View style={tw`flex-row flex-wrap gap-2 mb-4`}>
          {files.map(f => (
            <View key={f.uuid} style={tw`relative`}>
              <RNImage source={{ uri: f.url }} style={tw`w-16 h-16 rounded-lg`} />
              <TouchableOpacity
                onPress={() => removeFile(f.uuid)}
                style={tw`absolute -top-1 -right-1 bg-white rounded-full border border-gray-200 w-5 h-5 items-center justify-center`}
              >
                <Ionicons name="close" size={12} color="#374151" />
              </TouchableOpacity>
            </View>
          ))}
          <View style={tw`w-16 h-16 rounded-lg border border-dashed border-gray-300 items-center justify-center`}>
            {uploading ? (
              <ActivityIndicator size="small" color="#6366f1" />
            ) : (
              <>
                {/* @ts-ignore — DOM input via react-native-web */}
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={onFileSelect}
                  style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }}
                />
                <Ionicons name="add" size={20} color="#9ca3af" />
              </>
            )}
          </View>
        </View>

        <IconButton
          disabled={isSubmitting || uploading}
          title="Crear reporte"
          style={tw`self-end`}
          onPress={handleSubmit(onSave)}
        />
      </View>
    </Modal>
  );
}

function PillChoice({ label, active, onPress }: any) {
  return (
    <TouchableOpacity
      onPress={onPress}
      style={tw.style(`px-3 py-1.5 rounded-full border`, active ? 'border-transparent bg-primary' : 'border-gray-200 bg-white')}
    >
      <Text style={tw.style(`text-xs font-500`, active ? 'text-white' : 'text-gray-700')}>{label}</Text>
    </TouchableOpacity>
  );
}
